import { TABLE_COLUMN_GAP } from './Table.styled';
import type { TableColumn, TableColumnPin, TablePinSide, TableRow } from './types';

/**
 * Floor used when a flexible column declares no `minWidth` of its own. Only
 * applies to `getTableMinWidth` — it never narrows a column's grid track.
 */
export const DEFAULT_COLUMN_MIN_WIDTH = 120;

/** `140` and `'140px'` are the same track; `'1.5fr'` and `undefined` are not fixed. */
const parsePxWidth = (width?: number | string): number | null => {
  if (typeof width === 'number') return width;
  if (typeof width === 'string') {
    const match = /^(\d+(?:\.\d+)?)px$/.exec(width.trim());
    if (match) return Number(match[1]);
  }
  return null;
};

const resolveColumnMinWidth = <T extends TableRow>(column: TableColumn<T>): number =>
  column.minWidth ?? parsePxWidth(column.width) ?? DEFAULT_COLUMN_MIN_WIDTH;

/**
 * Grid track for one column.
 *
 * A fixed px width is already both floor and ceiling, so it passes through
 * untouched. Everything else keeps its declared track unless the column opts
 * into `minWidth` / `maxWidth`, in which case it becomes a `minmax()` — this is
 * what stops a flexible column from swallowing the row when few columns are
 * visible (GP-3770).
 */
const buildColumnTrack = <T extends TableRow>(column: TableColumn<T>): string => {
  const fixed = parsePxWidth(column.width);
  if (fixed !== null) return `${fixed}px`;

  // A numeric width is always fixed, so anything left here is a CSS track string.
  const track = typeof column.width === 'string' ? column.width : '1fr';
  if (column.minWidth === undefined && column.maxWidth === undefined) return track;

  const min = `${column.minWidth ?? 0}px`;
  const max = column.maxWidth !== undefined ? `${column.maxWidth}px` : track;

  return `minmax(${min}, ${max})`;
};

/** Empty track that soaks up space no real column is allowed to take. */
const FILLER_TRACK = 'minmax(0, 1fr)';

/** A column can grow only if its track is fractional and nothing caps it. */
const columnCanGrow = <T extends TableRow>(column: TableColumn<T>): boolean => {
  if (parsePxWidth(column.width) !== null) return false;
  if (column.maxWidth !== undefined) return false;

  return (typeof column.width === 'string' ? column.width : '1fr').includes('fr');
};

/**
 * Whether the row needs a filler track to reach the full container width.
 *
 * When every column is fixed or capped — e.g. a capped name column next to a
 * fixed actions column, which is what's left once the optional columns are
 * cleared — the tracks stop short of the container and the trailing actions
 * column drifts inward. `Table` inserts the filler just before the last column
 * and anchors that column to the final track so it stays flush right.
 *
 * Only applies when that last column is meant to sit at the right edge (pinned
 * or right-aligned). Otherwise trailing slack is the correct layout and pushing
 * the last column over would be wrong.
 */
export const getPinSide = (pinned: TableColumnPin | undefined): TablePinSide | undefined => {
  if (pinned === 'start') return 'start';
  if (pinned === true || pinned === 'end') return 'end';
  return undefined;
};

export const needsFillerTrack = <T extends TableRow>(columns: TableColumn<T>[]): boolean => {
  if (columns.length < 2) return false;

  const last = columns[columns.length - 1];
  if (getPinSide(last.pinned) !== 'end' && last.align !== 'right') return false;

  return !columns.some(columnCanGrow);
};

export const buildGridTemplate = <T extends TableRow>(columns: TableColumn<T>[]): string => {
  const tracks = columns.map(buildColumnTrack);
  if (!needsFillerTrack(columns)) return tracks.join(' ');

  tracks.splice(tracks.length - 1, 0, FILLER_TRACK);

  return tracks.join(' ');
};

/**
 * Narrowest the table can render the given columns without squeezing any of
 * them below its minimum, gaps included.
 *
 * Pass the result as `minTableWidth` on tables whose visible columns change at
 * runtime. A hardcoded constant sized for the full column set keeps forcing
 * that width once columns are hidden, which leaves the remaining flexible
 * tracks to divide up far more space than they should (GP-3770).
 */
export const getTableMinWidth = <T extends TableRow>(
  columns: TableColumn<T>[],
  gap: number = TABLE_COLUMN_GAP,
): number => {
  // The filler collapses to 0 when there's no surplus, but it still sits behind
  // one more gap, so reserve that too or the row overflows by exactly one gap.
  const gapCount = Math.max(0, columns.length - 1) + (needsFillerTrack(columns) ? 1 : 0);

  return (
    columns.reduce((total, column) => total + resolveColumnMinWidth(column), 0) + gap * gapCount
  );
};
